import { useMemo } from 'react'

import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { usePageChainId } from '@/domain/hooks/usePageChainId'
import { useMarketInfoQuery } from '@/domain/market-info/useMarketInfo'
import { useHyperCoreSpotMetaQuery } from '@/domain/wallet/hypercore/useHyperCoreSpotMeta'
import { useMarketWalletInfoForMarketQuery } from '@/domain/wallet/useMarketWalletInfo'
import { useTokensInfoQuery } from '@/domain/wallet/useTokens/useTokensInfo'
import { useIsolatedPairs } from '@/features/isolated/logic/useIsolatedPairs'
import { formatExtraTokens, formatTokenInfoAssets } from '@/features/side-drawer/logic/utils'

import type { UsePortfolioEvmBalancesResult } from './types'

export function usePortfolioEvmBalances({
  chainId: chainIdOverride,
  refetchInterval,
}: { chainId?: number; refetchInterval?: number } = {}): UsePortfolioEvmBalancesResult {
  const { chainId: pageChainId } = usePageChainId()
  const chainId = chainIdOverride ?? pageChainId
  const nativeAssetInfo = getNativeAssetInfo(chainId)

  const { marketInfo, isPending: isPooledMarketLoading } = useMarketInfoQuery({ chainId, refetchInterval })
  const walletInfo = useMarketWalletInfoForMarketQuery({ chainId, marketInfo, refetchInterval })
  const { pairs, isLoading: isIsolatedPairsLoading } = useIsolatedPairs()
  const { assets: hyperCoreAssets, isPending: isHyperCoreAssetsLoading } = useHyperCoreSpotMetaQuery()

  const tokensFormatted = useMemo(
    () =>
      formatTokenInfoAssets({
        hyperCoreAssets,
        nativeAssetInfo,
        walletInfo,
        isolatedPairs: pairs,
        isLoading: isIsolatedPairsLoading || isHyperCoreAssetsLoading || walletInfo.isLoading,
      }),
    [hyperCoreAssets, isHyperCoreAssetsLoading, isIsolatedPairsLoading, nativeAssetInfo, pairs, walletInfo],
  )

  const { tokensInfo, isPending: isExtraEvmTokensLoading } = useTokensInfoQuery({
    tokens: tokensFormatted,
    chainId,
  })

  const extraTokens = useMemo(
    () =>
      formatExtraTokens({
        tokensInfo,
        pairs,
      }),
    [pairs, tokensInfo],
  )

  const balances = useMemo(
    () => [...walletInfo.walletBalances, ...extraTokens],
    [extraTokens, walletInfo.walletBalances],
  )

  const loading = useMemo(
    () => ({
      pooledMarket: isPooledMarketLoading,
      evmWallet: walletInfo.isLoading,
      hyperCoreAssets: isHyperCoreAssetsLoading,
      isolatedPairs: isIsolatedPairsLoading,
      extraEvmTokens: tokensFormatted.length > 0 && isExtraEvmTokensLoading,
    }),
    [
      isExtraEvmTokensLoading,
      isHyperCoreAssetsLoading,
      isIsolatedPairsLoading,
      isPooledMarketLoading,
      tokensFormatted.length,
      walletInfo.isLoading,
    ],
  )

  const isLoading = useMemo(() => Object.values(loading).some(Boolean), [loading])

  return useMemo(
    () => ({
      balances,
      walletInfo,
      marketInfo,
      isolatedPairs: pairs,
      loading,
      isLoading,
    }),
    [balances, isLoading, loading, marketInfo, pairs, walletInfo],
  )
}
